import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createQuoteSchema, type QuoteResponse } from '@k-management/shared';
import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Field } from '../../components/Field.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { ClientPicker } from './ClientPicker.js';
import { quoteKeys, useSubjectSearch } from './quotes-ui.js';

/**
 * New quote (proposal §4): it starts as a draft, with the client already chosen
 * or with only the name of the proposal; the number arrives when it is issued.
 */
export function NewQuotePage() {
  usePageTitle('Nuovo preventivo');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const subjects = useSubjectSearch(true);
  const [draft, setDraft] = useState({
    // Coming back from Anagrafica, the subject just entered is already chosen.
    clientId: params.get('cliente'),
    referenceName: '',
    validityDays: '30',
    notes: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const set = (key: keyof typeof draft, value: string | null) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const create = useMutation({
    mutationFn: (body: unknown) => apiRequest<QuoteResponse>('/quotes', { method: 'POST', body }),
    onSuccess: async (quote) => {
      await queryClient.invalidateQueries({ queryKey: quoteKeys.all });
      navigate(`/preventivatore/${quote.id}`, { replace: true });
    },
    onError: (error: Error) => showServerError(error, setErrors, setFormError),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    const client = subjects.data?.find((subject) => subject.id === draft.clientId);
    const { data, errors: found } = validate(createQuoteSchema, {
      clientId: draft.clientId,
      referenceName: draft.referenceName || client?.name || null,
      validityDays: draft.validityDays,
      notes: draft.notes || null,
    });
    setErrors(found);
    if (!data) return;
    if (!data.clientId && !data.referenceName) {
      setErrors({ referenceName: 'Scegli un cliente o scrivi il nome della proposta' });
      return;
    }
    create.mutate(data);
  };

  return (
    <section className="stack narrow-page">
      <div className="page-header">
        <div>
          <h1>Nuovo preventivo</h1>
          <p className="muted">
            È per uso interno: costi e ricavi si valutano qui. Approvato, diventa un’offerta per il
            cliente con il numero OFF.
          </p>
        </div>
      </div>
      <form className="card stack" onSubmit={submit}>
        <ClientPicker
          value={draft.clientId}
          onChange={(clientId) => set('clientId', clientId)}
          error={errors.clientId}
          hint="Si può scegliere anche dopo, prima di approvare."
          returnTo="/preventivatore/nuovo"
        />
        <Field
          label="Oggetto"
          error={errors.referenceName}
          hint="Il titolo dell’offerta, come lo legge il cliente."
        >
          <input
            value={draft.referenceName}
            maxLength={200}
            onChange={(event) => set('referenceName', event.target.value)}
            placeholder="Percorso di coaching 2026"
          />
        </Field>
        <div className="row">
          <Field label="Validità (giorni)" error={errors.validityDays}>
            <input
              type="number"
              min={1}
              max={365}
              value={draft.validityDays}
              onChange={(event) => set('validityDays', event.target.value)}
            />
          </Field>
        </div>
        <Field label="Note" error={errors.notes}>
          <textarea
            rows={3}
            maxLength={2000}
            value={draft.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={() => navigate('/preventivatore')}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={create.isPending}>
            {create.isPending ? 'Creazione…' : 'Crea bozza'}
          </button>
        </div>
      </form>
    </section>
  );
}
